import {
  FormsMaterializedQuestionQuestionTypeEnum,
  QuestionsMyTrainingSurveysFragment,
  SectionsMyTrainingSurveysFragment,
} from 'generated/resources/graphql'
import { v4 as uuid } from 'uuid'

import {
  AnswerType,
  QuestionnaireResponseContent,
  QuestionnaireResponseElement,
} from 'modules/questionnaires/lib/types'

export type AnswerMyTrainings = {
  id: string
  answer?: string
  comment?: string
  materializedQuestion?: {
    id?: string
  }
  materializedQuestionOptions?: {
    id: string
    value: string
  }[]
}
//We do this in order to get the position of the section/question
function getPosition(item: QuestionnaireResponseElement) {
  if ('title' in item) {
    //if the item is a section, it will have a title prop and the position will be in the first level
    return item.position ?? 0
  } else {
    return item.question.position ?? 0
  }
}

export function questionnaireAnswerFunctions() {
  function decodeAnswerType(answer: AnswerType) {
    if ('answerChoice' in answer) {
      return answer.answerChoice
    } else if ('answerText' in answer) {
      return answer.answerText
    } else if ('answerInt' in answer) {
      return answer.answerInt?.toString()
    } else if ('answerRating' in answer) {
      return answer.answerRating?.value ? answer.answerRating?.value.toString() : undefined
    } else {
      return answer.answerInt?.toString()
    }
  }
  function extractComments(answer: AnswerType) {
    if ('answerRating' in answer) {
      return answer.answerRating?.comment ?? undefined
    }
  }
  const encodeQuestionnaireResponseContent: (
    sections: SectionsMyTrainingSurveysFragment[],
    questions: QuestionsMyTrainingSurveysFragment[],
    answers: AnswerMyTrainings[]
  ) => QuestionnaireResponseContent = (sections, questions, answers) => {
    function encodeAnswerType({
      answer,
      type,
      options,
    }: {
      options?: { id: string; value: string }[]
      answer: AnswerMyTrainings
      type: FormsMaterializedQuestionQuestionTypeEnum
    }): AnswerType {
      switch (type) {
        case 'text':
          return { answerText: answer.answer ?? '', uuid: answer.id }
        case 'number':
          return { answerInt: Number(answer.answer), uuid: answer.id }
        case 'rating':
          return {
            answerRating: {
              value: answer.answer ? Number(answer.answer) : undefined,
              comment: answer.comment,
            },
            uuid: answer.id,
          }
        case 'multiple_choice':
          return {
            answerChoice: options?.map((option) => option.id),
            answerChoiceAbstraction: options?.map((option) => {
              return { id: option.id, value: option.value }
            }),
            uuid: answer.id,
          }
      }
    }
    const questionnaireQuestions = questions.map((question) => {
      const answer = answers.find((answer) => answer.materializedQuestion?.id === question.id)
      return {
        answer: answer
          ? encodeAnswerType({
              answer,
              type: question.questionType,
              options: answer.materializedQuestionOptions?.map((option) => {
                return { id: option.id, value: option.value ?? '' }
              }),
            })
          : null,
        question: {
          title: question.label,
          mandatory: question.mandatory,
          maxChoices: question.maxChoices,
          choiceOptions: question.materializedQuestionOptions.map((option) => option.id),
          choiceOptionsAbstraction: question.materializedQuestionOptions.map((option) => {
            return { id: option.id, value: option.label }
          }),
          answerType: question.questionType,
          uuid: question.id,
          answer: answer?.answer,
          scale: question.scale,
          position: question.position,
          withComment: question.withComment,
        },
      }
    })
    const questionnaireSections = sections.map((section) => {
      return {
        title: section.title ?? '',
        position: section.position,
        uuid: uuid(),
        questions: section.materializedQuestions.map((question) => {
          const answer = answers.find((answer) => answer.materializedQuestion?.id === question.id)
          return {
            answer: answer
              ? encodeAnswerType({
                  answer,
                  type: question.questionType,
                  options: answer.materializedQuestionOptions?.map((option) => {
                    return { id: option.id, value: option.value ?? '' }
                  }),
                })
              : null,
            question: {
              title: question.label,
              mandatory: question.mandatory,
              maxChoices: question.maxChoices,
              choiceOptions: question.materializedQuestionOptions.map((option) => option.id),
              choiceOptionsAbstraction: question.materializedQuestionOptions.map((option) => {
                return { id: option.id, value: option.label }
              }),
              answerType: question.questionType,
              uuid: question.id,
              answer: answer?.answer,
              scale: question.scale,
              position: question.position,
              withComment: question.withComment,
            },
          }
        }),
      }
    })
    const questionnaire = [...questionnaireQuestions, ...questionnaireSections]
    return questionnaire.sort((itemA, itemB) => getPosition(itemA) - getPosition(itemB))
  }

  return {
    encodeQuestionnaireResponseContent,
    decodeAnswerType,
    extractComments,
  }
}
